/**
 * Page-level hook for the campaign detail view.
 *
 * The campaign header query is unconditional — it backs the page shell. The
 * five roster-tab queries exist for ONE purpose: giving
 * `useCampaignDeepLinkPanel` per-tab rows so a `?panel`/`?creator` share link
 * can find its creator across tabs. Fetching five tabs on every page load to
 * serve a link that is usually absent is pure waste, so they are gated behind
 * `rosterEnabled` (see CampaignDetailPage, which passes the link's presence).
 * When a creator isn't in the loaded rows the hook already falls back to the
 * server-side `/roster/by-username` resolver, so gating loses no capability.
 */
import { useQuery } from '@tanstack/react-query';
import { campaignKeys } from '../queryKeys';
import { createCampaignDetailApi } from '../api/campaignQueries';

const api = createCampaignDetailApi();
const PAGE = { page: 1, limit: 20 } as const;
const STALE_ROSTER = 5 * 60_000;
const STALE_DETAIL = 10 * 60_000;

export interface UseCampaignDetailOptions {
  /** Fetch the per-tab roster rows used for creator deep-link resolution.
   *  Defaults to true so existing callers keep their behaviour. */
  rosterEnabled?: boolean;
}

export function useCampaignDetail(
  campaignId: string,
  { rosterEnabled = true }: UseCampaignDetailOptions = {},
) {
  const enabled = !!campaignId;
  const rosterQueriesEnabled = enabled && rosterEnabled;

  const campaign = useQuery({
    queryKey: campaignKeys.detail(campaignId),
    queryFn: () => api.getCampaign(campaignId),
    enabled,
    staleTime: STALE_DETAIL,
  });

  const creators = useQuery({
    queryKey: campaignKeys.rosterTab(campaignId, 'creators', PAGE),
    queryFn: () => api.listCreators(campaignId, PAGE),
    enabled: rosterQueriesEnabled,
    staleTime: STALE_ROSTER,
  });

  const finalized = useQuery({
    queryKey: campaignKeys.rosterTab(campaignId, 'finalized', PAGE),
    queryFn: () => api.listFinalized(campaignId, PAGE),
    enabled: rosterQueriesEnabled,
    staleTime: STALE_ROSTER,
  });

  const scripts = useQuery({
    queryKey: campaignKeys.rosterTab(campaignId, 'scripts', PAGE),
    queryFn: () => api.listScript(campaignId, PAGE),
    enabled: rosterQueriesEnabled,
    staleTime: STALE_ROSTER,
  });

  const content = useQuery({
    queryKey: campaignKeys.rosterTab(campaignId, 'content', PAGE),
    queryFn: () => api.listContent(campaignId, PAGE),
    enabled: rosterQueriesEnabled,
    staleTime: STALE_ROSTER,
  });

  const live = useQuery({
    queryKey: campaignKeys.rosterTab(campaignId, 'live-content', PAGE),
    queryFn: () => api.listLiveContent(campaignId, PAGE),
    enabled: rosterQueriesEnabled,
    staleTime: STALE_ROSTER,
  });

  return { campaign, creators, finalized, scripts, content, live };
}
